import { Button, Input, Label, Textarea } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PageHeader } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import type { IExportPreset } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { NOT_ARRIVED } from '../../components/field-controls';
import { useAdminData } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { searchFrom, type ICardLinkState } from '../../components/participant-link';
import { LIST_SURVEYS, listSurveyLabel } from '../../lists';
import { importPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { firmLabel, useTeamFirms } from '../export-layouts/layout';

/**
 * New import — the wizard (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §5.2): one form where the import will be drawn, four things — the survey, the file, a layout
 * (optional) and a note (optional) — and **Check the file**, `POST /admin/imports` as a multipart
 * form (`client.ts`' one `FormData` path). survey-api answers 202 with the import `queued`: the
 * record takes the address of the import made (`replace`, so Back does not reopen a sent form) and
 * says *Checking the file…* while the Worker reads it.
 *
 * **The file is judged by nobody here** (`PhotoInput`'s rule): `accept` narrows the picker to the two
 * types survey-api reads, and its limits are said in words — 10 MB, 10,000 rows, 200 columns — never
 * checked. A 413 says the file is too large; a 400 is said at the box each problem names (`file`,
 * `survey`, `presetId`, `note`, the form itself as `body`); an answer that never arrived is said so,
 * with the list read again, since the import may have been made.
 */

/** survey-api's bound on a note (`EditBatch.NoteMaxLength`) — said, never enforced. */
const NOTE_MAX = 2000;

type FormProblems = Partial<Record<'file' | 'survey' | 'presetId' | 'note' | 'body', string>>;

export function NewImportForm({ survey: initial }: { survey: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const reread = useListReread();
  const [survey, setSurvey] = React.useState(
    LIST_SURVEYS.some((one) => one.code === initial) ? initial : (LIST_SURVEYS[0]?.code ?? 'FA')
  );
  const [file, setFile] = React.useState<Nullable<File>>(null);
  const [presetId, setPresetId] = React.useState('');
  const [note, setNote] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [problems, setProblems] = React.useState<FormProblems>({});
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const firms = useTeamFirms();
  const presets = useAdminData(
    async (signal) => ({ survey, presets: (await listsApi.presets({ survey }, signal)) ?? [] }),
    [survey]
  );
  const layouts: IExportPreset[] =
    presets.data?.survey === survey ? presets.data.presets : [];

  const check = async (event: React.FormEvent) => {
    event.preventDefault();
    if (file === null) {
      setProblems({ file: 'Choose the file to check.' });
      return;
    }
    setSending(true);
    setProblems({});
    setSaid(null);
    try {
      const made = await listsApi.upload({
        file,
        survey,
        note: note.trim() || undefined,
        presetId: presetId || undefined
      });
      reread();
      navigate(importPath(made.id), {
        replace: true,
        state: { search: searchFrom(location.state), list: true } satisfies ICardLinkState
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 413) {
        setProblems({
          file:
            error.problem?.type === ProblemType.importTooLarge && error.problem.title
              ? `${error.problem.title} Nothing was stored.`
              : 'The file is larger than 10 MB. Nothing was stored.'
        });
      } else if (error instanceof ApiError && error.status === 400 && error.problem?.problems?.length) {
        const placed: FormProblems = {};
        for (const problem of error.problem.problems) {
          const at = (['file', 'survey', 'presetId', 'note'] as const).find((name) => name === problem.target) ?? 'body';
          placed[at] = placed[at] ? `${placed[at]} ${problem.reason}` : sayFormProblem(at, problem.reason);
        }
        setProblems(placed);
      } else if (error instanceof ApiError && error.status === 403) {
        setSaid('Your Team Portal roles do not include importing files.');
      } else if (!(error instanceof ApiError) || error.status >= 500) {
        reread();
        setSaid(`${NOT_ARRIVED} See whether the import is in the list beside before checking the file again.`);
      } else {
        setSaid(refusalMessage(error, 'The file could not be sent.'));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader title="New import" crumb="Import" />
      <div className="grid max-w-2xl gap-5 px-6 py-6">
        <form className="grid gap-5" onSubmit={(event) => void check(event)} noValidate data-new-import>
          <div className="grid gap-1.5">
            <Label htmlFor="import-survey">Survey</Label>
            <select
              id="import-survey"
              value={survey}
              disabled={sending}
              onChange={(event) => {
                setSurvey(event.target.value);
                setPresetId('');
              }}
              className="h-9 max-w-72 rounded-md border border-border bg-white px-2 text-sm"
              aria-describedby={problems.survey ? 'import-survey-problem' : undefined}
            >
              {LIST_SURVEYS.map((one) => (
                <option key={one.code} value={one.code}>
                  {one.label}
                </option>
              ))}
            </select>
            <Problem id="import-survey-problem" text={problems.survey} />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="import-file">File</Label>
            <Input
              id="import-file"
              type="file"
              accept=".xlsx,.csv"
              disabled={sending}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setProblems((now) => ({ ...now, file: undefined }));
              }}
              aria-describedby="import-file-rule"
            />
            <p id="import-file-rule" className="text-sm text-muted-foreground">
              An Excel file (its first sheet) or a CSV, up to 10 MB, 10,000 rows and 200 columns.
              The column headings are matched to field names; <em>Internal ID</em> matches each row
              to its record (or a CRD column, where there is no Internal ID).
            </p>
            <Problem id="import-file-problem" text={problems.file} />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="import-layout">Match the headings through a layout</Label>
            <select
              id="import-layout"
              value={presetId}
              disabled={sending || layouts.length === 0}
              onChange={(event) => setPresetId(event.target.value)}
              className="h-9 max-w-96 rounded-md border border-border bg-white px-2 text-sm"
            >
              <option value="">
                {presets.data?.survey !== survey
                  ? presets.error
                    ? 'No layout — the layouts could not be read'
                    : 'Reading the layouts…'
                  : layouts.length === 0
                    ? `No layout — ${listSurveyLabel(survey)} has none`
                    : 'No layout — the headings as written'}
              </option>
              {layouts.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.firm ? `${preset.name} · ${firmLabel(firms.data, preset.firm)}` : preset.name}
                </option>
              ))}
            </select>
            {presets.error && presets.data?.survey !== survey && (
              <p className="text-sm text-muted-foreground">
                The layouts could not be read: {presets.error}
              </p>
            )}
            <Problem id="import-layout-problem" text={problems.presetId} />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="import-note">Note (optional)</Label>
            <Textarea
              id="import-note"
              value={note}
              disabled={sending}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              aria-describedby="import-note-rule"
            />
            <p id="import-note-rule" className="text-sm text-muted-foreground">
              Kept with every change the file makes, on each history. {NOTE_MAX.toLocaleString('en-US')}{' '}
              characters at most
              {note.trim().length > 0 && ` — ${note.trim().length.toLocaleString('en-US')} typed`}.
            </p>
            <Problem id="import-note-problem" text={problems.note} />
          </div>

          <Problem id="import-body-problem" text={problems.body} />
          {said && (
            <p role="alert" className="text-sm text-destructive">
              {said}
            </p>
          )}
          <div>
            <Button type="submit" disabled={sending}>
              {sending ? 'Sending the file…' : 'Check the file'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

/** A refusal of one box, in survey-api's words after the box's name. */
function sayFormProblem(at: keyof FormProblems, reason: string): string {
  const name = { file: 'The file', survey: 'The survey', presetId: 'The layout', note: 'The note', body: 'The form' }[at];
  return `${name} ${reason}.`.replace(/\.\.$/, '.');
}

function Problem({ id, text }: { id: string; text?: string }) {
  if (!text) return null;
  return (
    <p id={id} role="alert" className="text-sm text-destructive">
      {text}
    </p>
  );
}
